import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  MEDREFILL_INSTITUTION_CODE_PATH,
  MEDREFILL_ORDER_FOR_OTHERS_PATH,
  MEDREFILL_ORDER_TYPE_PATH,
  MEDREFILL_PATH,
  MEDREFILL_POLYCLINICS_PATH,
  MEDREFILL_REFILL_ADD_PARTICULARS_PATH,
  MEDREFILL_REFILL_COLLECTIONS_PATH,
  MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH,
  MEDREFILL_REFILL_PAYMENT_MODE_PATH,
  MEDREFILL_REFILL_PRESCRIBED_LIST_PATH,
  MEDREFILL_REFILL_PRESCRIBED_MEDICATION_PATH,
  MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH,
  MEDREFILL_REFILL_REFILL_OPTIONS_PATH,
  MEDREFILL_REFILL_SORT_FILTER_PATH,
  MEDREFILL_REFILL_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_REFILL_SUMMARY_PATH,
  MEDREFILL_REFILL_TNC_PATH,
  MEDREFILL_REFILL_UNSUCCESSFUL_REQUEST_PATH,
  MEDREFILL_TOPUP_ADD_PARTICULARS_PATH,
  MEDREFILL_TOPUP_CLINICS_PATH,
  MEDREFILL_TOPUP_COLLECTIONS_PATH,
  MEDREFILL_TOPUP_PAYMENT_MODE_PATH,
  MEDREFILL_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH,
  MEDREFILL_TOPUP_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_TOPUP_SUMMARY_PATH,
  MEDREFILL_TOPUP_TNC_PATH,
  MEDREFILL_TOPUP_UNSUCCESSFUL_REQUEST_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

const SelectInstitution = lazy(() => import('./pages/SelectInstitution/SelectInstitution'));
const OriginalDetails = lazy(() => import('./pages/OriginalDetails/OriginalDetails'));
const SelectPolyclinic = lazy(() => import('./pages/SelectPolyclinic/SelectPolyclinic'));
const SelectMedicationOrderType = lazy(() => import('./pages/SelectMedicationOrderType/SelectMedicationOrderType'));
const SelectPaymentMode = lazy(() => import('./pages/SelectPaymentMode/SelectPaymentMode'));
const Prescriptions = lazy(() => import('./pages/Prescriptions/Prescriptions'));
const RefillOptions = lazy(() => import('./pages/RefillOptions/RefillOptions'));
const AddParticulars = lazy(() => import('./pages/AddParticulars/AddParticulars'));
const MedRefillSummary = lazy(() => import('./pages/MedRefillSummary/MedRefillSummary'));
const Collections = lazy(() => import('./pages/Collections/Collections'));
const Clinic = lazy(() => import('./pages/Clinic/Clinic'));
const PrescribedMedications = lazy(() => import('./pages/PrescribedMedications/PrescribedMedications'));
const SortAndFilter = lazy(() => import('./pages/SortAndFilter/SortAndFilter'));
const SuccessfulMedRequest = lazy(() => import('./pages/SuccessfulMedRequest/SuccessfulMedRequest'));
const UnsuccessfulMedRequest = lazy(() => import('./pages/UnsuccessfulMedRequest/UnsuccessfulMedRequest'));
const PrescribedList = lazy(() => import('./pages/PrescribedList/PrescribedList'));
const OrderForOthersLanding = lazy(() => import('./pages/OrderForOthersLanding/OrderForOthersLanding'));
const RepeatPrescriptions = lazy(() => import('./pages/RepeatPrescriptions/RepeatPrescriptions'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));

export const MEDREFILL_ROUTES: RouteConfig[] = [
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectInstitution isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectInstitution isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_INSTITUTION_CODE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <SelectPolyclinic isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_POLYCLINICS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectMedicationOrderType isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_ORDER_TYPE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <OriginalDetails isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RefillOptions isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_REFILL_OPTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_PAYMENT_MODE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_PAYMENT_MODE_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Prescriptions isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_ADD_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_ADD_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_COLLECTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_COLLECTIONS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_TNC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_TNC_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Clinic isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_CLINICS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PrescribedMedications />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_PRESCRIBED_MEDICATION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SortAndFilter />,
    displayTitle: () => 'Sort & Filter',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_SORT_FILTER_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <SuccessfulMedRequest isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_SUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <SuccessfulMedRequest isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_SUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <UnsuccessfulMedRequest isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_UNSUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <UnsuccessfulMedRequest isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_UNSUCCESSFUL_REQUEST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <PrescribedList />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_REFILL_PRESCRIBED_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_PATH,
    component: () => <OrderForOthersLanding />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_ORDER_FOR_OTHERS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RepeatPrescriptions isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: MEDREFILL_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
];
